import { useState, useEffect } from 'react';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
} from 'chart.js';
import { Brain, TrendingUp, Target, Award, BarChart3, Lock, CheckCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import BackButton from '../components/BackButton';
import { api } from '../services/api';
import type { QuizStats } from '../types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

export default function Statistics() {
  const { theme } = useTheme();
  const { font } = useFont();
  const [stats, setStats] = useState<QuizStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getQuizStats()
      .then(setStats)
      .catch(error => {
        console.error('Error fetching quiz stats:', error);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" style={{ fontFamily: font.family }}>
        <div className="text-center">
          <div className={`w-12 h-12 border-4 border-${theme.primary} border-t-transparent rounded-full animate-spin mx-auto mb-4`} />
          <p className="text-gray-600">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50" style={{ fontFamily: font.family }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <BackButton />
          </div>
          <div className="text-center">
            <Brain className={`h-16 w-16 text-gray-400 mx-auto mb-4`} />
            <h2 className="text-2xl font-bold text-gray-600 mb-2">Aucune statistique disponible</h2>
            <p className="text-gray-500">Commencez par faire quelques QCM pour voir vos statistiques ici.</p>
          </div>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const difficultyData = {
    labels: ['Facile', 'Moyen', 'Difficile'],
    datasets: [{
      data: [stats.by_difficulty.easy, stats.by_difficulty.medium, stats.by_difficulty.difficult],
      backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
      borderWidth: 0
    }]
  };

  // Separate the module data into two different charts with appropriate scales
  const moduleScoreData = {
    labels: Object.keys(stats.by_module),
    datasets: [
      {
        label: 'Score moyen (%)',
        data: Object.values(stats.by_module).map(module => module.average),
        backgroundColor: `rgba(251, 165, 181, 0.8)`,
        borderColor: `rgb(251, 165, 181)`,
        borderWidth: 2,
        yAxisID: 'y'
      }
    ]
  };

  const moduleSuccessData = {
    labels: Object.keys(stats.by_module),
    datasets: [
      {
        label: 'Taux de réussite (%)',
        data: Object.values(stats.by_module).map(module =>
          module.total > 0 ? Math.round((module.success_100 / module.total) * 100) : 0
        ),
        backgroundColor: `rgba(34, 197, 94, 0.8)`,
        borderColor: `rgb(34, 197, 94)`,
        borderWidth: 2,
        yAxisID: 'y'
      }
    ]
  };

  const scoreChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.parsed.y}%`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value: number) => `${value}%`
        }
      },
      x: {
        grid: {
          display: false,
        }
      }
    },
  };

  const successChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.parsed.y}%`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value: number) => `${value}%`
        }
      },
      x: {
        grid: {
          display: false,
        }
      }
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
        }
      },
    },
    cutout: '50%',
  };

  // Prepare score history chart data
  const scoreHistoryData = {
    labels: stats.score_history.map(entry =>
      new Date(entry.date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      })
    ),
    datasets: [{
      label: 'Score (%)',
      data: stats.score_history.map(entry => entry.score),
      borderColor: `rgb(251, 165, 181)`,
      backgroundColor: `rgba(251, 165, 181, 0.1)`,
      tension: 0.4,
      pointRadius: 6,
      pointHoverRadius: 8,
      fill: true,
      pointBackgroundColor: `rgb(251, 165, 181)`,
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
    }]
  };

  const scoreHistoryOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `Score: ${context.parsed.y}%`
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 12
          }
        }
      },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value: number) => `${value}%`
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        }
      }
    },
  };

  const getUnlockedModeLabel = (mode: string): string => {
    switch (mode) {
      case 'medium':
        return 'Niveau Moyen';
      case 'free':
        return 'Mode Libre';
      default:
        return mode;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: font.family }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <BackButton />
        </div>

        <div className="flex items-center mb-8">
          <Brain className={`h-8 w-8 text-${theme.text} mr-3`} />
          <h1 className={`text-3xl font-bold text-${theme.text}`}>
            Statistiques des QCM
          </h1>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className={`h-12 w-12 text-${theme.text}`} />
              <span className="text-3xl font-bold">{stats.total_qcm}</span>
            </div>
            <h3 className="text-lg font-semibold mb-1">QCM réalisés</h3>
            <p className="text-gray-600">Total des évaluations</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <Target className={`h-12 w-12 text-${theme.text}`} />
              <span className="text-3xl font-bold">{Math.round(stats.success_rate)}%</span>
            </div>
            <h3 className="text-lg font-semibold mb-1">Taux de réussite</h3>
            <p className="text-gray-600">QCM à 100%</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className={`h-12 w-12 text-${theme.text}`} />
              <span className="text-3xl font-bold">{Math.round(stats.average_score)}%</span>
            </div>
            <h3 className="text-lg font-semibold mb-1">Score moyen</h3>
            <p className="text-gray-600">Moyenne générale</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <Award className={`h-12 w-12 text-${theme.text}`} />
              <span className="text-3xl font-bold">{1 + (stats.mode_progress.medium.unlocked ? 1 : 0) + (stats.mode_progress.free.unlocked ? 1 : 0)}</span>
            </div>
            <h3 className="text-lg font-semibold mb-1">Modes débloqués</h3>
            <p className="text-gray-600">Niveaux accessibles</p>
          </div>
        </div>

        {/* Unlocked Modes */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold mb-6">Progression et modes débloqués</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg border-2 border-${theme.primary} bg-${theme.secondary}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold flex items-center">
                  <CheckCircle className={`h-5 w-5 mr-2 text-${theme.primary}`} />
                  Mode Progressif
                </h3>
              </div>
              <p className="text-sm text-gray-600">Niveau facile débloqué par défaut</p>
            </div>

            <div className={`p-4 rounded-lg border-2 ${stats.mode_progress.medium.unlocked ? `border-${theme.primary} bg-${theme.secondary}` : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold flex items-center">
                  {stats.mode_progress.medium.unlocked ? (
                    <CheckCircle className={`h-5 w-5 mr-2 text-${theme.primary}`} />
                  ) : (
                    <Lock className="h-5 w-5 mr-2 text-gray-400" />
                  )}
                  Niveau Moyen
                </h3>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  {stats.mode_progress.medium.unlocked
                    ? 'Débloqué ! Mix de difficultés disponible'
                    : `Progression : ${stats.mode_progress.medium.current}/${stats.mode_progress.medium.required} QCM faciles à 100%`
                  }
                </p>
                {!stats.mode_progress.medium.unlocked && (
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-${theme.primary} transition-all duration-300`}
                      style={{ width: `${Math.min((stats.mode_progress.medium.current / stats.mode_progress.medium.required) * 100, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={`p-4 rounded-lg border-2 ${stats.mode_progress.free.unlocked ? `border-${theme.primary} bg-${theme.secondary}` : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold flex items-center">
                  {stats.mode_progress.free.unlocked ? (
                    <CheckCircle className={`h-5 w-5 mr-2 text-${theme.primary}`} />
                  ) : (
                    <Lock className="h-5 w-5 mr-2 text-gray-400" />
                  )}
                  Mode Libre
                </h3>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  {stats.mode_progress.free.unlocked
                    ? 'Débloqué ! Choix libre des modules et difficultés'
                    : `Progression : ${stats.mode_progress.free.current}/${stats.mode_progress.free.required} QCM moyens à 100%`
                  }
                </p>
                {!stats.mode_progress.free.unlocked && (
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-${theme.primary} transition-all duration-300`}
                      style={{ width: `${Math.min((stats.mode_progress.free.current / stats.mode_progress.free.required) * 100, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Score History Chart */}
        {stats.score_history && stats.score_history.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-semibold mb-6">Évolution des scores</h2>
            <div className="h-80">
              <Line data={scoreHistoryData} options={scoreHistoryOptions} />
            </div>
            <p className="text-sm text-gray-500 mt-4 text-center">
              Progression de vos scores au fil du temps
            </p>
          </div>
        )}

        {/* Questionnaires Block */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold mb-6">Questionnaires</h2>
          <div className={`bg-${theme.secondary} rounded-lg p-6 border border-${theme.primary}`}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">QCM réussis à 100% en facile</span>
                <span className="text-xl font-bold">{stats.qcm_summary.success_100_by_difficulty.facile}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">QCM réussis à 100% en moyen</span>
                <span className="text-xl font-bold">{stats.qcm_summary.success_100_by_difficulty.moyen}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">QCM réussis à 100% en difficile</span>
                <span className="text-xl font-bold">{stats.qcm_summary.success_100_by_difficulty.difficile}</span>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800">Pourcentage de réussite sur l'ensemble des QCM</span>
                  <span className={`text-2xl font-bold text-${theme.text}`}>{Math.round(stats.qcm_summary.overall_success_rate)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Module Details */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-6">Questions par module et difficulté</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {Object.entries(stats.success_questions_by_module)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([moduleCode, moduleStats]) => (
              <div key={moduleCode} className="bg-gray-50 rounded-lg p-4">
                <h3 className={`font-semibold text-lg mb-4 text-center text-${theme.text}`}>{moduleCode}</h3>

                <div className={`bg-${theme.secondary} rounded-lg p-4 border border-${theme.primary}`}>
                  <div className="space-y-3">
                    {/* Questions faciles */}
                    {moduleStats.facile && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">Questions faciles réussies</span>
                          <span className="text-sm font-bold">{moduleStats.facile.correct}/{moduleStats.facile.available}</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full bg-${theme.primary} transition-all duration-300`}
                            style={{ width: `${moduleStats.facile.available > 0 ? (moduleStats.facile.correct / moduleStats.facile.available) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Questions moyennes */}
                    {moduleStats.moyen && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">Questions moyennes réussies</span>
                          <span className="text-sm font-bold">{moduleStats.moyen.correct}/{moduleStats.moyen.available}</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full bg-${theme.primary} transition-all duration-300`}
                            style={{ width: `${moduleStats.moyen.available > 0 ? (moduleStats.moyen.correct / moduleStats.moyen.available) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {/* Questions difficiles */}
                    {moduleStats.difficile && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">Questions difficiles réussies</span>
                          <span className="text-sm font-bold">{moduleStats.difficile.correct}/{moduleStats.difficile.available}</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full bg-${theme.primary} transition-all duration-300`}
                            style={{ width: `${moduleStats.difficile.available > 0 ? (moduleStats.difficile.correct / moduleStats.difficile.available) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}